import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { SubscriptionType, SubscriptionTypeFormData } from "@/types/subscriptionType";
import { getUserGymId } from "@/utils/gymAuth";
import {
  createSubscriptionType,
  updateSubscriptionType,
} from "@/services/edgeFunctionWrappers/subscriptionTypesWrapper";
import { listCalendars } from "@/services/edgeFunctionWrappers/calendarsWrapper";
import type { GymCalendar } from "@/types/types";
import { useCurrency } from "@/contexts/CurrencyContext";

const buildFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t("subscriptionTypeForm.nameRequired")),
    description: z.string().optional(),
    sessions_total: z.coerce.number().min(1, t("subscriptionTypeForm.minSessions")),
    validity_days: z.coerce.number().nullable().optional(),
    price: z.coerce.number().min(0, t("subscriptionTypeForm.pricePositive")),
    jokers_total: z.coerce.number().min(0, t("subscriptionTypeForm.minJokers")),
    is_active: z.boolean(),
    calendar_ids: z.array(z.string()).optional(),
  });

type FormValues = z.infer<ReturnType<typeof buildFormSchema>>;

interface SubscriptionTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingType: SubscriptionType | null;
  onSuccess: () => void;
}

export function SubscriptionTypeFormDialog({
  open,
  onOpenChange,
  editingType,
  onSuccess,
}: SubscriptionTypeFormDialogProps) {
  const { t } = useTranslation(["subscriptions", "bookingui"]);
  const { currency } = useCurrency();
  const gymId = getUserGymId();
  const formSchema = buildFormSchema((key: string) => t(key, { ns: "bookingui" }));

  const [calendars, setCalendars] = useState<GymCalendar[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      sessions_total: 1,
      validity_days: 30,
      price: 0,
      jokers_total: 0,
      is_active: true,
      calendar_ids: [],
    },
  });

  useEffect(() => {
    if (!gymId || !open) return;
    listCalendars(gymId, false).then(setCalendars).catch(() => setCalendars([]));
  }, [gymId, open]);

  // Brouillon par type (subscription-type:{id} / :new). L'effet reset re-tire
  // à chaque open/close : mergeDraftOver relit le store en direct et le reset
  // pose isDirty=false, donc aucun brouillon fantôme n'en découle.
  const { clearDraft, mergeDraftOver } = useFormDraft<FormValues>("subscription-type", form, {
    entityId: editingType?.id ?? null,
    restoreOnMount: false,
  });

  useEffect(() => {
    if (editingType) {
      form.reset(mergeDraftOver({
        name: editingType.name,
        description: editingType.description || "",
        sessions_total: editingType.sessions_total,
        validity_days: editingType.validity_days,
        price: editingType.price,
        jokers_total: editingType.jokers_total,
        is_active: editingType.is_active,
        calendar_ids: editingType.calendar_ids ?? [],
      }));
    } else {
      form.reset(mergeDraftOver({
        name: "",
        description: "",
        sessions_total: 1,
        validity_days: 30,
        price: 0,
        jokers_total: 0,
        is_active: true,
        calendar_ids: [],
      }));
    }
  }, [editingType, form, open, mergeDraftOver]);

  const onSubmit = async (values: FormValues) => {
    if (!gymId) return;

    try {
      const data: SubscriptionTypeFormData = {
        name: values.name,
        description: values.description,
        sessions_total: values.sessions_total,
        validity_days: values.validity_days || null,
        price: values.price,
        jokers_total: values.jokers_total,
        is_active: values.is_active,
        calendar_ids: values.calendar_ids && values.calendar_ids.length > 0 ? values.calendar_ids : undefined,
      };

      if (editingType) {
        await updateSubscriptionType(gymId, editingType.id, data);
        toast.success(t("types.updateSuccess"));
      } else {
        await createSubscriptionType(gymId, data);
        toast.success(t("types.createSuccess"));
      }

      clearDraft();
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || t("types.saveError"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {editingType ? t("types.editTitle") : t("types.createTitle")}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("types.name")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("subscriptionTypeForm.namePlaceholder", { ns: "bookingui" })} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("types.description")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("subscriptionTypeForm.descriptionPlaceholder", { ns: "bookingui" })}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="sessions_total"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("types.sessions")}</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="validity_days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("types.validityDays")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        placeholder="30"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          field.onChange(val ? parseInt(val) : null);
                        }}
                      />
                    </FormControl>
                    <FormDescription>{t("types.validityHint")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("types.price")} ({currency})</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="jokers_total"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("types.jokers")}</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {calendars.length > 0 && (
              <FormField
                control={form.control}
                name="calendar_ids"
                render={({ field }) => (
                  <FormItem className="rounded-lg border p-3 space-y-2">
                    <FormLabel>{t("types.accessibleCalendars", "Calendriers accessibles")}</FormLabel>
                    <FormDescription>
                      {field.value?.length === 0
                        ? t("types.universalAccess", "Accès universel (tous les calendriers)")
                        : t("types.restrictedCalendars", "Accès limité aux calendriers cochés.")}
                    </FormDescription>
                    <div className="grid gap-2 pt-1">
                      {calendars.map((cal) => (
                        <div key={cal.id} className="flex items-center space-x-2">
                          <FormControl>
                            <Checkbox
                              id={`cal-${cal.id}`}
                              checked={field.value?.includes(cal.id) ?? false}
                              onCheckedChange={(checked) => {
                                const next = checked
                                  ? [...(field.value ?? []), cal.id]
                                  : (field.value ?? []).filter((id) => id !== cal.id);
                                field.onChange(next);
                              }}
                            />
                          </FormControl>
                          <label
                            htmlFor={`cal-${cal.id}`}
                            className="flex items-center gap-2 text-sm font-normal cursor-pointer"
                          >
                            {cal.color && (
                              <span
                                className="inline-block h-3 w-3 rounded-full shrink-0"
                                style={{ backgroundColor: cal.color }}
                              />
                            )}
                            {cal.name}
                          </label>
                        </div>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>{t("types.isActive")}</FormLabel>
                    <FormDescription>{t("types.isActiveHint")}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              {/* Annulation explicite : le brouillon est jeté. */}
              <Button type="button" variant="outline" onClick={() => { clearDraft(); onOpenChange(false); }}>
                {t("types.cancel")}
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting
                  ? t("types.saving")
                  : editingType
                  ? t("types.save")
                  : t("types.create")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
